import { usePWA } from '../contexts/PWAContext';

interface SyncOperation {
  id: string;
  type: 'contribution' | 'member' | 'expense' | 'event' | 'benefit';
  data: any;
  url: string;
  token?: string;
  timestamp: number;
  priority: 'high' | 'medium' | 'low';
  retryCount: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
}

interface ConnectionQuality {
  type: 'excellent' | 'good' | 'poor' | 'offline';
  latency: number;
  bandwidth: number;
  effectiveType: string;
}

interface UserActivity {
  type: 'active' | 'idle' | 'away';
  lastActivity: number;
  isActive: boolean;
}

interface BatteryStatus {
  level: number;
  isCharging: boolean;
  chargingTime: number;
  dischargingTime: number;
}

class SmartSyncScheduler {
  private operations: Map<string, SyncOperation> = new Map();
  private connectionQuality: ConnectionQuality = {
    type: 'good',
    latency: 100,
    bandwidth: 1000,
    effectiveType: '4g'
  };
  private userActivity: UserActivity = {
    type: 'active',
    lastActivity: Date.now(),
    isActive: true
  };
  private batteryStatus: BatteryStatus = {
    level: 1.0,
    isCharging: true,
    chargingTime: 0,
    dischargingTime: Infinity
  };
  
  private syncInterval: NodeJS.Timeout | null = null;
  private activityTimeout: NodeJS.Timeout | null = null;
  private isSyncing = false;
  
  constructor() {
    this.setupMonitoring();
    this.startMonitoring();
  }
  
  private setupMonitoring() {
    // Monitor user activity
    const handleActivity = () => {
      this.userActivity.lastActivity = Date.now();
      this.userActivity.isActive = true;
      this.userActivity.type = 'active';
      
      // Clear idle timeout
      if (this.activityTimeout) {
        clearTimeout(this.activityTimeout);
      }
      
      // Set idle timeout (5 minutes)
      this.activityTimeout = setTimeout(() => {
        this.userActivity.type = 'idle';
        this.userActivity.isActive = false;
      }, 5 * 60 * 1000);
    };
    
    // Monitor connection quality
    const handleConnectionChange = () => {
      this.measureConnectionQuality();
    };
    
    // Monitor battery status
    const handleBatteryChange = () => {
      this.updateBatteryStatus();
    };
    
    // Add event listeners
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);
    
    // Monitor Network Information API changes
    if ('connection' in navigator) {
      (navigator as any).connection.addEventListener('change', handleConnectionChange);
    }
    
    // Monitor battery status
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        this.updateBatteryStatusFromAPI(battery);
        battery.addEventListener('chargingchange', handleBatteryChange);
        battery.addEventListener('levelchange', handleBatteryChange);
      });
    }
  }
  
  private startMonitoring() {
    // Start periodic sync checks
    this.syncInterval = setInterval(() => {
      this.checkSyncOpportunities();
    }, 30000); // Check every 30 seconds
    
    // Initial measurements
    this.measureConnectionQuality();
    this.updateBatteryStatus();
  }
  
  public addOperation(operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount' | 'status'>): string {
    const id = `${operation.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const syncOperation: SyncOperation = {
      ...operation,
      id,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending'
    };
    
    this.operations.set(id, syncOperation);
    this.checkSyncOpportunities();
    
    return id;
  }
  
  public removeOperation(id: string): boolean {
    return this.operations.delete(id);
  }
  
  public getOperations(): SyncOperation[] {
    return Array.from(this.operations.values());
  }
  
  public getOperation(id: string): SyncOperation | undefined {
    return this.operations.get(id);
  }
  
  private async checkSyncOpportunities() {
    if (this.isSyncing || this.connectionQuality.type === 'offline') {
      return;
    }
    
    // Get operations that should sync now
    const operationsToSync = this.getOperationsToSync();
    
    if (operationsToSync.length > 0) {
      await this.executeSyncBatch(operationsToSync);
    }
  }
  
  private getOperationsToSync(): SyncOperation[] {
    const now = Date.now();
    const operations = Array.from(this.operations.values())
      .filter(op => op.status === 'pending')
      .sort((a, b) => {
        // Sort by priority first, then by timestamp
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return a.timestamp - b.timestamp;
      });
    
    const suitableOperations: SyncOperation[] = [];
    
    for (const operation of operations) {
      if (this.shouldSyncOperation(operation)) {
        suitableOperations.push(operation);
        
        // Limit batch size based on connection quality
        if (suitableOperations.length >= this.getMaxBatchSize()) {
          break;
        }
      }
    }
    
    return suitableOperations;
  }
  
  private shouldSyncOperation(operation: SyncOperation): boolean {
    // Check if operation should sync based on current conditions
    
    // High priority operations sync more aggressively
    if (operation.priority === 'high') {
      return this.connectionQuality.type !== 'offline' && 
             this.userActivity.isActive;
    }
    
    // Medium priority operations need good connection and user activity
    if (operation.priority === 'medium') {
      return this.connectionQuality.type === 'excellent' || 
             (this.connectionQuality.type === 'good' && this.userActivity.isActive);
    }
    
    // Low priority operations are more conservative
    if (operation.priority === 'low') {
      return this.connectionQuality.type === 'excellent' && 
             this.userActivity.type === 'active' &&
             this.batteryStatus.level > 0.3 &&
             this.batteryStatus.isCharging;
    }
    
    return false;
  }
  
  private getMaxBatchSize(): number {
    switch (this.connectionQuality.type) {
      case 'excellent': return 5;
      case 'good': return 3;
      case 'poor': return 1;
      default: return 0;
    }
  }
  
  private async executeSyncBatch(operations: SyncOperation[]): Promise<void> {
    if (operations.length === 0) return;
    
    this.isSyncing = true;
    
    try {
      // Execute operations in parallel (limited by batch size)
      const promises = operations.map(op => this.executeSyncOperation(op));
      await Promise.allSettled(promises);
    } finally {
      this.isSyncing = false;
    }
  }
  
  private async executeSyncOperation(operation: SyncOperation): Promise<void> {
    operation.status = 'syncing';
    
    try {
      const response = await fetch(operation.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${operation.token}`
        },
        body: JSON.stringify(operation.data)
      });
      
      if (response.ok) {
        this.operations.delete(operation.id);
        this.notifySyncSuccess(operation);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      operation.retryCount++;
      operation.status = 'failed';
      
      if (operation.retryCount < 3) {
        // Exponential backoff for retries
        const delay = Math.min(1000 * Math.pow(2, operation.retryCount), 30000);
        setTimeout(() => {
          operation.status = 'pending';
          this.checkSyncOpportunities();
        }, delay);
      } else {
        this.notifySyncFailure(operation, error as Error);
        this.operations.delete(operation.id);
      }
    }
  }
  
  private async measureConnectionQuality(): Promise<void> {
    const startTime = performance.now();
    
    try {
      const response = await fetch('/api/ping', { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      const endTime = performance.now();
      const latency = endTime - startTime;
      
      // Estimate bandwidth (simplified)
      const bandwidth = this.estimateBandwidth();
      
      // Determine connection type
      let type: ConnectionQuality['type'] = 'good';
      let effectiveType = '4g';
      
      if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        effectiveType = conn.effectiveType || '4g';
        
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          type = 'poor';
        } else if (effectiveType === '3g') {
          type = 'good';
        } else {
          type = 'excellent';
        }
      } else {
        // Fallback based on latency and bandwidth
        if (latency > 500 || bandwidth < 100) {
          type = 'poor';
        } else if (latency > 200 || bandwidth < 1000) {
          type = 'good';
        } else {
          type = 'excellent';
        }
      }
      
      this.connectionQuality = {
        type,
        latency,
        bandwidth,
        effectiveType
      };
    } catch (error) {
      this.connectionQuality.type = 'offline';
    }
  }
  
  private estimateBandwidth(): number {
    // Simplified bandwidth estimation
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      if (conn && conn.downlink) {
        return conn.downlink * 1000; // Convert to kbps
      }
    }
    
    // Fallback estimates based on connection type
    switch (this.connectionQuality.effectiveType) {
      case 'slow-2g': return 50;
      case '2g': return 250;
      case '3g': return 1500;
      case '4g': return 5000;
      default: return 1000;
    }
  }
  
  private updateBatteryStatus(): void {
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        this.updateBatteryStatusFromAPI(battery);
      });
    }
  }
  
  private updateBatteryStatusFromAPI(battery: any): void {
    this.batteryStatus = {
      level: battery.level,
      isCharging: battery.charging,
      chargingTime: battery.chargingTime,
      dischargingTime: battery.dischargingTime
    };
  }
  
  private notifySyncSuccess(operation: SyncOperation): void {
    // Dispatch custom event for successful sync
    window.dispatchEvent(new CustomEvent('sync-success', {
      detail: {
        operationId: operation.id,
        type: operation.type,
        timestamp: Date.now()
      }
    }));
  }
  
  private notifySyncFailure(operation: SyncOperation, error: Error): void {
    // Dispatch custom event for failed sync
    window.dispatchEvent(new CustomEvent('sync-failure', {
      detail: {
        operationId: operation.id,
        type: operation.type,
        error: error.message,
        timestamp: Date.now()
      }
    }));
  }
  
  public destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
  }
}

// Singleton instance
let syncScheduler: SmartSyncScheduler | null = null;

export const getSyncScheduler = (): SmartSyncScheduler => {
  if (!syncScheduler) {
    syncScheduler = new SmartSyncScheduler();
  }
  return syncScheduler;
};


// Export types for use in other components
export type { SyncOperation, ConnectionQuality, UserActivity, BatteryStatus };